import {
  CloseOutlined,
  DeleteFilled,
  DeleteOutlined,
  ExclamationCircleFilled,
  SaveOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import {
  Drawer,
  Button,
  message,
  Spin,
  Row,
  Col,
  Collapse,
  Typography,
  Table,
  ModalProps,
  Modal,
  notification,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useMatch, useSearchParams } from 'react-router-dom';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import PageTitle from '../../components/PageTitle';
import UserForm, { UserFormValues } from '../../components/UserForm';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { RecruiterCollection } from '../../types';
import styles from './EditUser.module.css';

interface ConfirmSoftDeleteModalProps extends ModalProps {
  userFullName: string;
  onSuccess?: () => void | Promise<unknown>;
}

const ConfirmSoftDeleteModal = ({ userFullName, ...props }: ConfirmSoftDeleteModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      okButtonProps={{
        htmlType: 'button',
      }}
      okText={t('Sim, desativar usuário')}
      title={
        <Typography.Text strong>
          <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 20 }} />
          <Trans>Atenção!</Trans>
        </Typography.Text>
      }
      {...props}
    >
      <Typography.Text>
        <Trans>Ao confirmar, o usuário</Trans>{' '}
        <Typography.Text strong>{userFullName}</Typography.Text>
        <Trans>será desativado. Tem certeza que deseja continuar?</Trans>
      </Typography.Text>
    </Modal>
  );
};

const ConfirmReactivateUserModal = ({ userFullName, ...props }: ConfirmSoftDeleteModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      okButtonProps={{
        htmlType: 'button',
      }}
      okText={t('Sim, ativar usuário')}
      title={
        <Typography.Text strong>
          <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 20 }} />
          <Trans>Atenção!</Trans>
        </Typography.Text>
      }
      {...props}
    >
      <Typography.Text>
        <Trans>Ao confirmar, o usuário</Trans>{' '}
        <Typography.Text strong>{userFullName}</Typography.Text>
        <Trans>será ativado novamente. Tem certeza que deseja continuar?</Trans>
      </Typography.Text>
    </Modal>
  );
};

const EditUser = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm<UserFormValues>();
  const { tenant, user_id } = useTypedParams({ tenant: String, user_id: Number });
  const queryClient = useQueryClient();
  const match = useMatch(':tenant/settings/cs-staff/users/');
  const [confirmUserSoftDeleteIsOpen, setConfirmUserSoftDeleteIsOpen] = useState<boolean>(false);
  const [confirmUserReactivateIsOpen, setConfirmUserReactivateIsOpen] = useState<boolean>(false);
  const [searchParams] = useSearchParams();

  const user = useAPIQuery(`/users/${user_id}/`);
  const config = useAPIQuery('/configs/tenant-configurations/');

  const recruitersPages = useInfiniteAPIQuery([
    '/recruiters/',
    {
      params: {
        user_id,
      },
    },
  ]);

  const matchCsUserPath = useMatch('/:tenant/settings/cs-staff/users/:user_id/change');

  const recruiters = usePrefetchPages(recruitersPages, (data) => data.results);

  const navigate = useTypedNavigate();

  const [setIsDirty] = useDiscardChangesModal(form);

  const navigateToUsers = useCallback(() => {
    if (matchCsUserPath && !config.data?.t001_enableCentralizedAuthIntegration) {
      navigate({
        pathname: '/:tenant/settings/cs-staff/users/',
        params: { tenant },
        search: searchParams,
      });
    } else {
      navigate({
        pathname: '/:tenant/settings/accessManagement/users/',
        params: { tenant },
        search: searchParams,
      });
    }
  }, [
    config.data?.t001_enableCentralizedAuthIntegration,
    matchCsUserPath,
    navigate,
    searchParams,
    tenant,
  ]);

  useEffect(() => {
    if (user.isFetched && match) {
      navigateToUsers();
    }
  }, [navigateToUsers, user, match]);

  const mutation = useAPIMutation(
    (variables: UserFormValues, api) => {
      return api.patch(`/users/${user_id}/`, {
        ...(config.data?.t001_enableCentralizedAuthIntegration
          ? {}
          : {
              full_name: variables.name || '',
              email: variables.email,
              ...(matchCsUserPath ? { username: variables.username } : {}),
            }),
        groups: variables.groups
          ? Array.isArray(variables.groups)
            ? variables.groups
            : [variables.groups]
          : [],
      });
    },
    {
      onSuccess: () => {
        setIsDirty(false);
        message.success(t('Usuário atualizado!'));
        navigateToUsers();
      },
      onError: (err, variables, context) => {
        // TODO: pog temporario arrumar dps
        if (
          err?.response?.data?.email?.[0] === 'usuário com este Endereço de e-mail já existe.' ||
          err?.response?.data?.email?.[0] === 'user with this Email address already exists.'
        ) {
          return notification.error({
            duration: 0,
            message: t('Atenção'),
            description: t('Já existe um usuário com este endereço de E-mail cadastrado.'),
          });
        }

        if (err?.response?.data?.username?.[0]) {
          return notification.error({
            duration: 0,
            message: t('Atenção'),
            description: err.response.data.username[0],
          });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
      onSettled: () => {
        queryClient.invalidateQueries('/users/');
        queryClient.invalidateQueries(`/users/${user_id}/`);
      },
    }
  );

  const deleteRecruiterMutation = useAPIMutation(
    (variables: { id: number }, api) => {
      return api.delete(`/recruiters/${variables.id}/`);
    },
    {
      onSuccess: () => {
        message.success(t('Vaga desmarcada'));
      },
      onSettled: () => {
        queryClient.invalidateQueries('/recruiters/');
      },
    }
  );

  const softDeleteUserMutation = useAPIMutation(
    (_, api) =>
      api.patch(`/users/${user_id}/`, {
        is_active: false,
      }),
    {
      onSuccess: () => {
        message.success(t('Usuário desativado com sucesso!'));
        navigateToUsers();
      },
      onSettled: () => {
        queryClient.invalidateQueries('/users/');
      },
    }
  );

  const reactivateUserMutation = useAPIMutation(
    (_, api) =>
      api.patch(`/users/${user_id}/`, {
        is_active: true,
      }),
    {
      onSuccess: () => {
        message.success(t('Usuário ativado com sucesso!'));
        navigate({ pathname: '/:tenant/settings/cs-staff/users/', params: { tenant } });
      },
      onSettled: () => {
        queryClient.invalidateQueries('/users/');
      },
    }
  );

  return (
    <>
      <ConfirmSoftDeleteModal
        key={`ConfirmSoftDeleteModal-${user_id}`}
        open={confirmUserSoftDeleteIsOpen}
        onOk={() => {
          setConfirmUserSoftDeleteIsOpen(false);
          softDeleteUserMutation.mutate({});
        }}
        onCancel={() => setConfirmUserSoftDeleteIsOpen(false)}
        onSuccess={() => {
          setConfirmUserSoftDeleteIsOpen(false);
        }}
        destroyOnClose
        userFullName={user.data?.full_name || '...'}
      />
      <ConfirmReactivateUserModal
        key={`ConfirmReactivateUserModal-${user_id}`}
        open={confirmUserReactivateIsOpen}
        onOk={() => {
          setConfirmUserReactivateIsOpen(false);
          reactivateUserMutation.mutate({});
        }}
        onCancel={() => setConfirmUserReactivateIsOpen(false)}
        onSuccess={() => {
          setConfirmUserReactivateIsOpen(false);
        }}
        destroyOnClose
        userFullName={user.data?.full_name || '...'}
      />
      <Drawer
        className={styles.drawer}
        data-testid="edit-user-drawer"
        title={
          <PageTitle
            title={user.data?.username || '...'}
            level={2}
            extra={[
              <Button key="close" type="text" onClick={() => navigateToUsers()} shape="circle">
                <CloseOutlined />
              </Button>,
            ]}
          />
        }
        maskClosable
        size="large"
        closable={false}
        width={720}
        footer={
          <Row gutter={[16, 16]}>
            <Col>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={mutation.isLoading || softDeleteUserMutation.isLoading}
                onClick={() => form?.submit()}
              >
                {' '}
                <Trans>Salvar</Trans>
              </Button>
            </Col>
            <Col>
              <Button
                loading={mutation.isLoading || softDeleteUserMutation.isLoading}
                onClick={() => navigateToUsers()}
              >
                <Trans>Fechar</Trans>
              </Button>
            </Col>
            <Col flex={1} />
            {config.data && !config.data.t001_enableCentralizedAuthIntegration && (
              <Col>
                {user.data?.is_active ? (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    loading={mutation.isLoading || softDeleteUserMutation.isLoading}
                    onClick={() => setConfirmUserSoftDeleteIsOpen(true)}
                  >
                    <Trans>Desativar</Trans>
                  </Button>
                ) : (
                  <Button
                    danger
                    icon={<UndoOutlined />}
                    loading={mutation.isLoading || softDeleteUserMutation.isLoading}
                    onClick={() => setConfirmUserReactivateIsOpen(true)}
                  >
                    <Trans>Reativar</Trans>
                  </Button>
                )}
              </Col>
            )}
          </Row>
        }
        open
        onClose={() => navigateToUsers()}
      >
        <Helmet>
          <title>
            {t('Editar | Perfil')}
            {user.data?.username || '...'} | {t('Recrutamento e Seleção')}
          </title>
        </Helmet>
        <ErrorResult error={user.error || recruitersPages.error}>
          <Spin spinning={user.isFetching || recruitersPages.isFetching}>
            <UserForm
              form={form}
              initialValues={{
                name: user.data?.full_name || '',
                username: user.data?.username,
                email: user.data?.email,
                groups: user.data?.groups,
              }}
              isActive={user.data?.is_active}
              disableUsername={matchCsUserPath ? false : !!user.data?.username}
              disableIfAuth={config.data?.t001_enableCentralizedAuthIntegration}
              layout="vertical"
              onValuesChange={() => setIsDirty(form.isFieldsTouched())}
              onFinish={(variables) => {
                mutation.mutate(variables);
              }}
            />
            <Collapse ghost expandIconPosition="end" defaultActiveKey={['recruiters']}>
              <Collapse.Panel
                key="recruiters"
                header={
                  <Typography.Text strong>
                    <Trans>Histórico de Vagas Marcadas</Trans>
                  </Typography.Text>
                }
              >
                <Table rowKey="id" dataSource={recruiters}>
                  <Table.Column
                    title={t('Nome da vaga')}
                    render={(record: RecruiterCollection) => (
                      <Typography.Text>{record.job_posting.name}</Typography.Text>
                    )}
                  />
                  <Table.Column
                    title={t('Função na vaga')}
                    render={(record: RecruiterCollection) => (
                      <>
                        {record.type === 'MANAGER' && (
                          <Typography.Text>
                            <Trans>Gestor</Trans>
                          </Typography.Text>
                        )}
                        {record.type === 'RELATED' && (
                          <Typography.Text>
                            <Trans>Envolvido</Trans>
                          </Typography.Text>
                        )}
                        {record.type === 'RECRUITER' && (
                          <Typography.Text>
                            <Trans>Recrutador</Trans>
                          </Typography.Text>
                        )}
                        {record.type === 'OBSERVER' && (
                          <Typography.Text>
                            <Trans>Observador</Trans>
                          </Typography.Text>
                        )}
                      </>
                    )}
                  />
                  <Table.Column
                    render={(record: RecruiterCollection) => (
                      <Button
                        type="text"
                        onClick={() => deleteRecruiterMutation.mutate({ id: record.id })}
                        disabled={!user.data?.is_active}
                      >
                        <DeleteFilled />
                      </Button>
                    )}
                  />
                </Table>
              </Collapse.Panel>
            </Collapse>
          </Spin>
        </ErrorResult>
      </Drawer>
    </>
  );
};

export default withRouteChange()(EditUser);
